import { computed } from 'mobx';
import get from 'lodash/get';

export default class AnalyticsStore {
  constructor(gdsUiState = {}, guestListStore = {}, experimentStore = {}) {
    this.gdsUiState = gdsUiState;
    this.guestListStore = guestListStore;
    this.experimentStore = experimentStore;
  }

  track(event = '', properties = {}, ...rest) {
    const trackParams = Object.assign({ platform: 'web', product: 'glm' }, properties);

    if (!this.isOnBoarding) {
      Object.assign(this.baseTrackingParams, this.selectedEventParams, trackParams);
    }
    if (window.analytics) {
      window.analytics.track(event, trackParams, ...rest);
    }
  }

  page(properties = {}) {
    const trackParams = Object.assign({ platform: 'web', product: 'glm' }, properties);

    if (window.analytics) {
      window.analytics.page(trackParams);
    }
  }

  identifyUser(memberId) {
    const traits = { guestCount: this.guestListStore.totalGuestCount };
    if (window.analytics) {
      window.analytics.identify(memberId, traits);
    }
  }

  static coverage(numerator, denominator) {
    return Math.round((numerator / denominator) * 100) || 0;
  }

  @computed
  get isOnBoarding() {
    return Object.keys(this.gdsUiState).length === 0 || Object.keys(this.guestListStore).length === 0;
  }

  @computed
  get baseTrackingParams() {
    const { viewType } = this.gdsUiState;
    const {
      gdsEvents,
      rsvpOnCount,
      rsvpPreference,
      rsvpQuestionCount,
      rsvpQuestionCategories,
      rsvpQuestionTypes,
      guestWedding,
    } = this.guestListStore;
    const weddingEvent = gdsEvents.weddingEvent;
    const { invitedGuests: invitedWeddingGuests } = this.eventDetails(weddingEvent);
    const invitedGuestsWithValidMailAddress = this.invitedGuestsWithValidMailAddress(weddingEvent);
    return {
      viewType,
      rsvpOnCount,
      publicRSVPList: get(guestWedding, ['allowGuestPreview']),
      hasRSVPQuestions: get(guestWedding, ['usesQuestions']),
      eventCount: gdsEvents.nonReceptionEvents.length,
      weddingRsvpStatus: get(weddingEvent, ['rsvp']) ? 'on' : 'off',
      weddingRSVPCoverage: get(weddingEvent, ['rsvpCoverage']),
      rsvpPreference: rsvpPreference,
      addressCoverage: AnalyticsStore.coverage(invitedGuestsWithValidMailAddress.length, invitedWeddingGuests.length),
      inviteCoverage: get(weddingEvent, ['inviteSentCoverage']),
      rsvpQuestionCount,
      rsvpQuestionCategories,
      rsvpQuestionTypes,
    };
  }

  eventDetails(event) {
    const { guests, households, invitations } = this.guestListStore;
    const invites = invitations.filter(invitation => invitation.eventId === event.id);
    const invitedGuests = invites.map(invitation => guests.find(guest => guest.id === invitation.guestId));
    let householdIds = invitedGuests.map(guest => get(guest, ['householdId']));
    householdIds = householdIds.filter((id, pos) => householdIds.indexOf(id) === pos);
    const invitedHouseholds = households.filter(household => householdIds.includes(household.id));
    return {
      invites,
      invitedGuests,
      householdIds,
      invitedHouseholds,
    };
  }

  invitedGuestsWithValidMailAddress(event) {
    const { invitedGuests, invitedHouseholds } = this.eventDetails(event);
    const invitedGuestsWithValidMailAddress = invitedGuests.filter(guest => {
      const guestHousehold = invitedHouseholds.find(household => household.id === get(guest, ['householdId']));
      return guestHousehold && guestHousehold.hasValidMailingAddress;
    });
    return invitedGuestsWithValidMailAddress;
  }

  @computed
  get selectedEventParams() {
    const { selectedEvent } = this.gdsUiState;
    const {
      households,
      guests,
      contactableHouseholdCount,
      contactableHouseholdCountEmail,
      contactableHouseholdCountPhone,
    } = this.guestListStore;
    let guestParams = {
      guestCount: guests.size,
      eventName: 'All Events',
      eventType: 'all events',
      householdCount: households.size,
      contactableHouseholdCount,
      contactableHouseholdCountEmail,
      contactableHouseholdCountPhone,
    };

    if (!selectedEvent) return guestParams;
    const { invitedGuests, invitedHouseholds } = this.eventDetails(selectedEvent);
    const invitedGuestsWithValidMailAddress = this.invitedGuestsWithValidMailAddress(selectedEvent);
    guestParams = {
      eventName: selectedEvent.name,
      eventType: selectedEvent.isWeddingEvent ? 'wedding event' : 'custom event',
      guestCount: invitedGuests.length,
      householdCount: invitedHouseholds.length,
      hasGuestListGroups: selectedEvent.groups && selectedEvent.groups.length > 0,
      contactableHouseholdCount: invitedHouseholds.filter(household => household.contactable).length,
      contactableHouseholdCountEmail: invitedHouseholds.filter(household => household.contactableByEmail).length,
      contactableHouseholdCountPhone: invitedHouseholds.filter(household => household.contactableByPhone).length,
      householdAddressCount: invitedHouseholds.filter(household => household.hasValidMailingAddress).length,
      addressCoverage: AnalyticsStore.coverage(invitedGuestsWithValidMailAddress.length, invitedGuests.length),
      inviteCoverage: selectedEvent.inviteSentCoverage,
    };
    return guestParams;
  }
}
